import React from "react";

import copyToClipboard from "copy-to-clipboard";
import _ from "lodash";
import { useHistory } from "react-router-dom";

import {
  RemoveApplicantModalProvider,
  MoveApplicantModalProvider,
  RemoveApplicantModalV2,
  MoveApplicantModal,
} from "manager/components/app-summary/modals";
import { useAppRow } from "manager/components/app-summary/use-app-row";
import { useIsViewOnlyUser } from "manager/hooks/useIsViewOnlyUser";
import Icon from "shared/components/Icon";
import { Popover } from "shared/components/Popover";
import Tooltip from "shared/components/Tooltip";
import {
  InfoIcon,
  MoveIcon,
  DeleteIcon,
  LinkOneIcon,
  UnblockIcon,
  MoreOneIcon,
} from "shared/components/vega/icons";
import { Timestamp } from "shared/components/vega/Timestamp";
import { APPLICATION_STATUSES } from "shared/config/constants";
import {
  useGetApplicantWorkflowRun,
  useListApplicantPayments,
} from "shared/hooks/api";
import { useFetchMissingLegacyApp } from "shared/hooks/useFetchMissingLegacyApp";
import { PAST_PAID_STATUS } from "shared/interfaces/types";
import {
  APPROVED_NOT_SUBMITTED_IN_VERO_LABEL,
  isVeroSubmittedApplication,
} from "shared/utils/deal-status";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import "../styles.css";

interface ApplicantInfoCardProps {
  id: string;
  applicant: any;
  legacyApp: any;
  shouldFetchMissingLegacyApp?: boolean;
  legacySummaryPending?: boolean;
  refetchDeal?: () => void;
  payments?: any[];
  dealId?: string | number;
  applicationStatus?: APPLICATION_STATUSES;
}

// Component for the progress bar
const ProgressBar = ({ percent }) => {
  const isComplete = percent === 100;

  return (
    <div
      className="app-summary-v2 vega progress-container"
      style={{ width: "100%" }}
    >
      <div className="vega progress-bar-wrapper" style={{ width: "100%" }}>
        <div
          className={`vega progress-bar-fill ${isComplete ? "success" : ""}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

const ApplicantInfoCard: React.FC<ApplicantInfoCardProps> = ({
  id,
  applicant,
  legacyApp,
  shouldFetchMissingLegacyApp = true,
  legacySummaryPending = false,
  refetchDeal,
  dealId,
  applicationStatus,
}) => {
  const { isViewOnly } = useIsViewOnlyUser();
  const hideMoveToAnotherApplicationButton = useVeroFlag(
    VeroFlag.hideMoveToAnotherApplicationButton
  );
  const hideRemoveFromApplicationButton = useVeroFlag(
    VeroFlag.hideRemoveFromApplicationButton
  );
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);
  const hideApplicationProgress = useVeroFlag(
    VeroFlag.hideApplicationProgressProduct1348
  );
  const pmsApprovedNoVeroEnabled = useVeroFlag(
    VeroFlag.useYardiApprovedNoVeroStatus
  );
  const decisionedApplicationLockEnabled = useVeroFlag(
    VeroFlag.enableDecisionedApplicationLockProduct2611
  );
  const history = useHistory();
  const copyInviteLinkDisabled = useVeroFlag(VeroFlag.copyInviteLinkDisabled);
  const { applicantWorkflow, isLoading: isWorkflowLoading } =
    useGetApplicantWorkflowRun(id);
  const { payments = [] } = useListApplicantPayments(id);
  // Use fetch hook to get complete legacyApp data for useAppRow
  // Don't fetch if applicant has error (deleted/not found)
  const { legacyAppData } = useFetchMissingLegacyApp({
    applicant,
    legacyApp,
    shouldFetch: shouldFetchMissingLegacyApp && !applicant?.error,
  });

  const legacyAppToUse = legacyAppData;
  const legacyAppId = applicant?.legacyId || legacyAppToUse?.appId;

  const isSubmitted =
    legacyAppToUse?.info?.isSubmitted ?? applicant?.isSubmitted;
  const wasSubmitted = legacyAppToUse?.info?.wasSubmitted;
  const applicantSubmittedAt =
    legacyAppToUse?.info?.submittedAt ?? applicant?.submittedAt;
  // PRODUCT-1522: the "Complete"/"Submitted" pills must reflect a *real* VERO
  // submission, not a PMS auto-approval. `isSubmitted` is true for Yardi-approved
  // applications (status APPROVED) even when the applicant never submitted in
  // VERO, so require an actual `submittedAt` timestamp. This keeps the pills in
  // agreement with the header banner and the Income/ID cards on the same screen.
  //
  // Gated behind the same flag as the banner/list de-confliction so the whole
  // screen flips together: when the flag is off the pills fall back to plain
  // `isSubmitted` (pre-PRODUCT-1522 behavior) and stay consistent with the
  // non-de-conflicted banner.
  const isVeroSubmitted = pmsApprovedNoVeroEnabled
    ? isVeroSubmittedApplication({
        isSubmitted,
        submittedAt: applicantSubmittedAt,
      })
    : Boolean(isSubmitted);
  // With de-confliction on, an applicant without a real VERO submission must not
  // reach the green "Complete"/100% Application state either (the progress can
  // hit 100% via `applicantWorkflow.done` or task/group completion without a
  // submit). Blocking it keeps the Application pill in agreement with the Status
  // pill and the header banner on Yardi-approved deals with no `submittedAt`.
  const veroBlocksComplete = pmsApprovedNoVeroEnabled && !isVeroSubmitted;
  const isApprovedWithoutVeroSubmit =
    decisionedApplicationLockEnabled &&
    applicationStatus === APPLICATION_STATUSES.approved &&
    !applicantSubmittedAt;
  const statusChipColors = isApprovedWithoutVeroSubmit
    ? {
        background: "#F0F9FF",
        color: "#0369A1",
        border: "#BAE6FD",
        dot: "#0EA5E9",
      }
    : isVeroSubmitted
    ? {
        background: "#ECFDF5",
        color: "#059669",
        border: "#A7F3D0",
        dot: "#10B981",
      }
    : wasSubmitted
    ? {
        background: "#FFFBEB",
        color: "#B45309",
        border: "#FDE68A",
        dot: "#F59E0B",
      }
    : {
        background: "#EEF2FF",
        color: "#4F46E5",
        border: "#C7D2FE",
        dot: "#6366F1",
      };

  const { infoProps, onCopyAppLink, unblock, modalProps } = useAppRow({
    dealId,
    appId: legacyAppId,
    info: legacyAppToUse?.info,
    yardi: legacyAppToUse?.yardi,
    refetchDeal,
  });

  const disableMoveAndDelete =
    _.get(applicant, "isPrimaryApplication") &&
    _.get(legacyAppToUse, "location")?.includes("yardi") &&
    !_.chain(payments)
      .filter((payment) => PAST_PAID_STATUS.includes(_.get(payment, "status")))
      .isEmpty()
      .value();

  const pastPaidMessage = disableMoveAndDelete
    ? "Action Restricted: The primary applicant cannot be changed or deleted after payment due to integration limitations with your PMS."
    : undefined;

  const partyType = applicant?.party
    ? _.capitalize(applicant.party.replace("-", " "))
    : "";
  const hasPayments = !_.isEmpty(payments);
  const hasPaid = _.some(payments, (payment) =>
    PAST_PAID_STATUS.includes(payment?.status)
  );

  const calculateProgress = () => {
    if ((!applicantWorkflow || !applicantWorkflow?.tasks) && !isVeroSubmitted) {
      return { percent: 0, status: "Not Started" };
    }

    if (!veroBlocksComplete && (applicantWorkflow?.done || isVeroSubmitted)) {
      return { percent: 100, status: "Complete" };
    }

    // Use group-level progress (matches applicant-flow section-based progress)
    const groups = applicantWorkflow?.tasks?.groups || [];
    const applicantFlow = groups.find((g: any) => g.id === "applicant-flow");
    const childGroups = applicantFlow?.groups || [];

    if (childGroups.length > 0) {
      const completedGroups = childGroups.filter((g: any) => {
        const nextTasks = g.next || [];
        const possibleTasks = g.possible || [];
        return nextTasks.length === 0 && possibleTasks.length === 0;
      }).length;
      const totalGroups = childGroups.length;
      const percent =
        totalGroups > 0 ? (completedGroups / totalGroups) * 100 : 0;

      return {
        percent,
        status:
          percent === 100
            ? "Complete"
            : percent === 0
            ? "Not Started"
            : `${Math.round(percent)}%`,
      };
    }

    // Fallback to task-level progress for non-V1 workflows
    const { complete = [], possible = [], next = [] } = applicantWorkflow.tasks;

    const completedTasks = complete.length;
    const totalTasks = completedTasks + possible.length + next.length;
    const percent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

    return {
      percent,
      status:
        percent === 100
          ? "Complete"
          : percent === 0
          ? "Not Started"
          : `${Math.round(percent)}%`,
    };
  };

  const { percent, status: progressStatus } = calculateProgress();
  const debugInfo = _.join(
    [applicant?.application, applicant?.id, applicant?.person],
    "\n"
  );

  return (
    <>
      <div
        className={`applicant-info-card banking-style ${
          partyType === "Primary applicant" ? "primary" : "co-applicant"
        }`}
        data-party-type={useDealsListUiRedesign ? partyType : undefined}
        style={{
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.04)",
          border: "1px solid #e5e7eb",
          borderLeft:
            partyType === "Primary applicant"
              ? "3px solid #111827"
              : partyType === "Guarantor"
              ? "3px solid #6B7280"
              : partyType === "Applicant"
              ? "3px solid #D1D5DB"
              : partyType === "Occupant"
              ? "3px solid #D1D5DB"
              : "3px solid #E5E7EB",
          borderRadius: "10px",
          background: "linear-gradient(to right, #ffffff 0%, #f9fafb 100%)",
          position: "relative",
          padding: useDealsListUiRedesign ? "16px 20px 16px 0px" : "16px 20px",
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          justifyContent: "space-between",
          gap: "0px",
          minHeight: "60px",
          width: "100%",
          boxSizing: "border-box",
          transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.05)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.04)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        {!isViewOnly && modalProps && (
          <RemoveApplicantModalV2
            {...modalProps}
            key={`remove-modal-${legacyAppId}`}
          />
        )}
        {!isViewOnly && modalProps && <MoveApplicantModal {...modalProps} />}

        {/* left content wraps as units; action buttons stay pinned right */}
        <div
          className={`vega applicant-main${hasPayments ? " has-payments" : ""}`}
          style={{ flex: "1 1 auto", minWidth: 0 }}
        >
          {/* Applicant Identity Section — badge + name row, email on its own line */}
          <div className="vega applicant-identity" style={{ minWidth: 0 }}>
            <div
              className="vega applicant-identity-title"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: 0,
              }}
            >
              {partyType && (
                <span
                  className="vega status-badge"
                  style={{
                    background:
                      partyType === "Primary applicant"
                        ? "#111827"
                        : partyType === "Guarantor"
                        ? "#F3F4F6"
                        : partyType === "Applicant"
                        ? "#F9FAFB"
                        : partyType === "Occupant"
                        ? "#F9FAFB"
                        : "#F9FAFB",
                    color:
                      partyType === "Primary applicant"
                        ? "white"
                        : partyType === "Guarantor"
                        ? "#374151"
                        : "#6B7280",
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "600",
                    letterSpacing: "0.025em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    border:
                      partyType === "Primary applicant"
                        ? "none"
                        : "1px solid #E5E7EB",
                  }}
                >
                  {partyType === "Primary applicant"
                    ? "PRIMARY"
                    : partyType.toUpperCase()}
                </span>
              )}
              <span
                className="vega applicant-name"
                style={{
                  fontWeight: "600",
                  color: "#111827",
                  whiteSpace: "nowrap",
                  userSelect: "text",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textDecorationColor: "transparent",
                  transition: "text-decoration-color 150ms ease",
                }}
                onClick={() => history.push(`/applications/${legacyAppId}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecorationColor = "#5B4EE8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecorationColor = "transparent";
                }}
              >
                {applicant?.firstName} {applicant?.lastName}
              </span>
            </div>
            {applicant?.email && (
              <a
                className="vega applicant-email"
                href={`mailto:${applicant.email}`}
                tabIndex={-1}
                title={applicant.email}
                style={{
                  display: "block",
                  color: "#6B7280",
                  textDecoration: "none",
                  cursor: "text",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  marginTop: "2px",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.stopPropagation()}
              >
                {applicant.email}
              </a>
            )}
          </div>

          {/* Statuses + timestamp */}
          <div
            className="vega applicant-status-group"
            style={{ display: "contents" }}
          >
            {/* Progress Section — absent until the workflow run answers:
                percent is a computed default, never rendered as data.
                PRODUCT-1348: hidden entirely; Status is the single indicator */}
            {!isWorkflowLoading && !hideApplicationProgress && (
              <div
                className="vega application-progress"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flex: "0 0 auto",
                  whiteSpace: "nowrap",
                }}
              >
                {/* Application Progress */}
                <span
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    whiteSpace: "nowrap",
                  }}
                >
                  Application:
                </span>
                {percent === 100 && !veroBlocksComplete ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: 600,
                      background: "#ECFDF5",
                      color: "#059669",
                      border: "1px solid #A7F3D0",
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#10B981",
                      }}
                    />
                    Complete
                  </span>
                ) : (
                  <>
                    <div
                      style={{
                        width: "60px",
                        height: "4px",
                        background: "#E5E7EB",
                        borderRadius: "2px",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          background: "#5B4EE8",
                          borderRadius: "2px",
                          width: `${percent}%`,
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#111827",
                        fontWeight: "500",
                      }}
                    >
                      {Math.round(percent)}%
                    </span>
                  </>
                )}
              </div>
            )}

            {/* STATUS Badge — absent until the legacy summary answers:
                "In Progress" is its fallback, not data */}
            {!legacySummaryPending && (
              <div
                className="vega application-status"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flex: "0 0 auto",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    whiteSpace: "nowrap",
                  }}
                >
                  Status:
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                    background: statusChipColors.background,
                    color: statusChipColors.color,
                    border: `1px solid ${statusChipColors.border}`,
                  }}
                >
                  <span
                    data-testid="application-status-dot"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: statusChipColors.dot,
                    }}
                  />
                  {isApprovedWithoutVeroSubmit
                    ? APPROVED_NOT_SUBMITTED_IN_VERO_LABEL
                    : isVeroSubmitted
                    ? "Submitted"
                    : wasSubmitted
                    ? "Editing"
                    : "In Progress"}
                </span>
              </div>
            )}
            {hasPayments && (
              <>
                <span
                  className="vega payment-status"
                  style={{
                    fontSize: "13px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "3px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {hasPaid ? (
                    <>
                      <Icon.CheckGreenSmall
                        className="vega icon"
                        style={{ width: "16px", height: "16px", flexShrink: 0 }}
                      />
                      <span style={{ color: "#10B981", fontSize: "13px" }}>
                        Paid
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon.OrangeExclamationSmall
                        className="vega icon"
                        style={{ width: "16px", height: "16px", flexShrink: 0 }}
                      />
                      <span style={{ color: "#F59E0B", fontSize: "13px" }}>
                        Unpaid
                      </span>
                    </>
                  )}
                </span>
              </>
            )}

            {/* Timestamp Section */}
            {applicant?.updatedAt && (
              <Tooltip
                title="Applicant last active on this date"
                theme="light"
                placement="top"
              >
                <div
                  className="vega timestamp-section"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: "0 0 auto",
                    color: "#6B7280",
                    fontWeight: "400",
                    cursor: "help",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Timestamp timestamp={applicant?.updatedAt} />
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Action Buttons Section */}
        <div
          className="vega applicant-actions"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            flex: "0 0 auto",
            alignSelf: "stretch",
            borderLeft: "1px solid #E5E7EB",
            padding: "8px 14px",
          }}
        >
          {useDealsListUiRedesign ? (
            /* Three-dot menu for redesign */
            <Popover
              placement="bottomRight"
              trigger="click"
              content={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    minWidth: "200px",
                  }}
                >
                  {legacyAppToUse?.info?.isBlocked &&
                    legacyAppToUse?.info?.permission?.unblock && (
                      <div
                        onClick={() =>
                          legacyAppToUse?.info?.permission?.unblock &&
                          unblock?.action()
                        }
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "#f3f4f6")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <UnblockIcon
                          style={{ width: "20px", height: "20px" }}
                        />
                        <span>Unblock applicant</span>
                      </div>
                    )}
                  {legacyAppToUse?.info?.inviteLink && !copyInviteLinkDisabled && (
                    <div
                      onClick={onCopyAppLink}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f3f4f6")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <LinkOneIcon style={{ width: "20px", height: "20px" }} />
                      <span>Copy invite link</span>
                    </div>
                  )}
                  <div
                    onClick={() => copyToClipboard(debugInfo)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f3f4f6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <InfoIcon style={{ width: "20px", height: "20px" }} />
                    <span>Copy applicant info</span>
                  </div>
                  {!isViewOnly && !hideMoveToAnotherApplicationButton && (
                    <div
                      onClick={() =>
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noMove &&
                        infoProps?.onMove()
                      }
                      style={{
                        padding: "8px 12px",
                        cursor:
                          !disableMoveAndDelete &&
                          !legacyAppToUse?.info?.permission?.noMove
                            ? "pointer"
                            : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "background 0.2s",
                        opacity:
                          !disableMoveAndDelete &&
                          !legacyAppToUse?.info?.permission?.noMove
                            ? 1
                            : 0.5,
                      }}
                      onMouseEnter={(e) => {
                        if (
                          !disableMoveAndDelete &&
                          !legacyAppToUse?.info?.permission?.noMove
                        ) {
                          e.currentTarget.style.background = "#f3f4f6";
                        }
                      }}
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <MoveIcon style={{ width: "20px", height: "20px" }} />
                      <span>Move to another application</span>
                    </div>
                  )}
                  {!isViewOnly && !hideRemoveFromApplicationButton && (
                    <div
                      onClick={() =>
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noRemove &&
                        infoProps?.onDelete()
                      }
                      style={{
                        padding: "8px 12px",
                        cursor:
                          !disableMoveAndDelete &&
                          !legacyAppToUse?.info?.permission?.noRemove
                            ? "pointer"
                            : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "background 0.2s",
                        opacity:
                          !disableMoveAndDelete &&
                          !legacyAppToUse?.info?.permission?.noRemove
                            ? 1
                            : 0.5,
                      }}
                      onMouseEnter={(e) => {
                        if (
                          !disableMoveAndDelete &&
                          !legacyAppToUse?.info?.permission?.noRemove
                        ) {
                          e.currentTarget.style.background = "#f3f4f6";
                        }
                      }}
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <DeleteIcon style={{ width: "20px", height: "20px" }} />
                      <span>Remove from application</span>
                    </div>
                  )}
                </div>
              }
            >
              <MoreOneIcon
                className="vega icon button"
                style={{
                  width: "27px",
                  height: "27px",
                  cursor: "pointer",
                }}
              />
            </Popover>
          ) : (
            /* Original icon layout */
            <>
              {legacyAppToUse?.info?.isBlocked &&
                legacyAppToUse?.info?.permission?.unblock && (
                  <Tooltip title="Unblock applicant" theme="light">
                    <UnblockIcon
                      className="vega icon button"
                      style={{
                        width: "27px",
                        height: "27px",
                      }}
                      onClick={() =>
                        legacyAppToUse?.info?.permission?.unblock &&
                        unblock?.action()
                      }
                    />
                  </Tooltip>
                )}

              {legacyAppToUse?.info?.inviteLink && !copyInviteLinkDisabled && (
                <Tooltip title="Copy invite link" theme="light">
                  <LinkOneIcon
                    role="button"
                    aria-label="Copy invite link"
                    className="vega icon button"
                    style={{
                      width: "27px",
                      height: "27px",
                    }}
                    onClick={onCopyAppLink}
                  />
                </Tooltip>
              )}

              <Popover
                placement="topRight"
                content={
                  <div className="vega content flex column no-gap">
                    <pre className="vega">{debugInfo}</pre>
                    <span
                      className="vega button actions end"
                      onClick={() => copyToClipboard(debugInfo)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && copyToClipboard(debugInfo)
                      }
                    >
                      copy
                    </span>
                  </div>
                }
              >
                <InfoIcon
                  className="vega icon button"
                  style={{
                    width: "27px",
                    height: "27px",
                  }}
                />
              </Popover>

              {!isViewOnly && !hideMoveToAnotherApplicationButton && (
                <Tooltip
                  title={
                    pastPaidMessage ||
                    legacyAppToUse?.info?.permission?.noMoveReason ||
                    "Move to another application"
                  }
                  theme="light"
                >
                  <MoveIcon
                    className="vega icon button"
                    style={{
                      width: "27px",
                      height: "27px",
                      cursor:
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noMove
                          ? "pointer"
                          : "not-allowed",
                      color:
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noMove
                          ? "#6B7280"
                          : "#D1D5DB",
                      transition: "color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noMove
                      ) {
                        e.currentTarget.style.color = "#374151";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noMove
                      ) {
                        e.currentTarget.style.color = "#6B7280";
                      }
                    }}
                    onClick={() =>
                      !disableMoveAndDelete &&
                      !legacyAppToUse?.info?.permission?.noMove &&
                      infoProps?.onMove()
                    }
                  />
                </Tooltip>
              )}

              {!isViewOnly && !hideRemoveFromApplicationButton && (
                <Tooltip
                  title={
                    pastPaidMessage ||
                    legacyAppToUse?.info?.permission?.noRemoveReason ||
                    "Remove from application"
                  }
                  theme="light"
                >
                  <DeleteIcon
                    className="vega icon button"
                    style={{
                      width: "27px",
                      height: "27px",
                      cursor:
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noRemove
                          ? "pointer"
                          : "not-allowed",
                      color:
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noRemove
                          ? "#6B7280"
                          : "#D1D5DB",
                      transition: "color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noRemove
                      ) {
                        e.currentTarget.style.color = "#374151";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (
                        !disableMoveAndDelete &&
                        !legacyAppToUse?.info?.permission?.noRemove
                      ) {
                        e.currentTarget.style.color = "#6B7280";
                      }
                    }}
                    onClick={() =>
                      !disableMoveAndDelete &&
                      !legacyAppToUse?.info?.permission?.noRemove &&
                      infoProps?.onDelete()
                    }
                  />
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
      {wasSubmitted && (
        <div
          style={{
            margin: "8px 0 0 0",
            padding: "12px 16px",
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderRadius: "8px",
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            fontSize: "13px",
            color: "#92400E",
            lineHeight: 1.5,
          }}
        >
          <span style={{ fontSize: "16px", flexShrink: 0, marginTop: "1px" }}>
            &#9888;
          </span>
          <span>
            <strong>Application withdrawn for edits.</strong> This applicant
            previously submitted on{" "}
            {applicantSubmittedAt
              ? `${new Date(applicantSubmittedAt).toLocaleDateString("en-US", {
                  month: "numeric",
                  day: "numeric",
                  year: "numeric",
                })} at ${new Date(applicantSubmittedAt).toLocaleTimeString(
                  "en-US",
                  {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  }
                )}`
              : "a prior date"}
            , then pulled the application back to make changes. It is not
            currently submitted for review.
          </span>
        </div>
      )}
    </>
  );
};

const ApplicantInfoCardWithActions: React.FC<ApplicantInfoCardProps> = (
  props
) => {
  return (
    <RemoveApplicantModalProvider>
      <MoveApplicantModalProvider>
        <ApplicantInfoCard {...props} />
      </MoveApplicantModalProvider>
    </RemoveApplicantModalProvider>
  );
};

export default ApplicantInfoCardWithActions;
